import { AbstractChatBox } from "@alice-and-bot/core/components";
import { embedScript } from "@alice-and-bot/core";
import type { JSX } from "preact";
import { ComponentChildren, FunctionalComponent } from "preact";
import { CSSProperties } from "preact/compat";
import { useLocation } from "preact-iso";
import { useEffect, useRef, useState } from "preact/hooks";
import { discordLink, privacyPath, termsPath } from "../../constants.ts";
import { signal } from "@preact/signals";
import { toast } from "react-hot-toast";
import { HexColorPicker } from "react-colorful";

let _hljs: typeof import("highlight.js/lib/core").default | null = null;
let _hljsReady: Promise<void> | null = null;

const ensureHighlightJs = () => {
  if (_hljsReady) return _hljsReady;
  _hljsReady = (async () => {
    const [mod, ts, json, bash] = await Promise.all([
      import("highlight.js/lib/core"),
      import("highlight.js/lib/languages/typescript"),
      import("highlight.js/lib/languages/json"),
      import("highlight.js/lib/languages/bash"),
    ]);
    _hljs = mod.default;
    _hljs.registerLanguage("typescript", ts.default);
    _hljs.registerLanguage("json", json.default);
    _hljs.registerLanguage("bash", bash.default);
  })();
  return _hljsReady;
};

const darkThemeCss = `.dark .hljs { color: #c9d1d9; background: #0d1117 }
  .dark .hljs-doctag, .dark .hljs-keyword, .dark .hljs-meta .hljs-keyword,
  .dark .hljs-template-tag, .dark .hljs-template-variable, .dark .hljs-type,
  .dark .hljs-variable.language_ { color: #ff7b72 }
  .dark .hljs-title, .dark .hljs-title.class_, .dark .hljs-title.class_.inherited__,
  .dark .hljs-title.function_ { color: #d2a8ff }
  .dark .hljs-attr, .dark .hljs-attribute, .dark .hljs-literal, .dark .hljs-meta,
  .dark .hljs-number, .dark .hljs-operator, .dark .hljs-variable, .dark .hljs-selector-attr,
  .dark .hljs-selector-class, .dark .hljs-selector-id { color: #79c0ff }
  .dark .hljs-regexp, .dark .hljs-string, .dark .hljs-meta .hljs-string { color: #a5d6ff }
  .dark .hljs-built_in, .dark .hljs-symbol { color: #ffa657 }
  .dark .hljs-comment, .dark .hljs-code, .dark .hljs-formula { color: #8b949e }
  .dark .hljs-name, .dark .hljs-quote, .dark .hljs-selector-tag,
  .dark .hljs-selector-pseudo { color: #7ee787 }
  .dark .hljs-subst { color: #c9d1d9 }
  .dark .hljs-section { color: #1f6feb; font-weight: bold }
  .dark .hljs-bullet { color: #f2cc60 }
  .dark .hljs-addition { color: #aff5b4; background-color: #033a16 }
  .dark .hljs-deletion { color: #ffdcd7; background-color: #67060c }`;

if (typeof document !== "undefined") {
  const styleEl = document.createElement("style");
  styleEl.textContent = darkThemeCss;
  document.head.appendChild(styleEl);
}

export const themeColors = {
  dark: {
    surface: "#111827",
    surfaceRaised: "#1f2937",
    text: "#f3f4f6",
    accent: "#3b82f6",
  },
  light: {
    surface: "#f9fafb",
    surfaceRaised: "#ffffff",
    text: "#1f2937",
    accent: "#2563eb",
  },
};

export const inputBaseClass =
  "border border-border-subtle-light dark:border-border-subtle rounded-lg px-3 py-2 bg-input-light dark:bg-input text-text-primary-light dark:text-text-primary focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent transition-colors font-body";

export const inputClass = `w-full max-w-md ${inputBaseClass}`;

export const labelClass =
  "block text-sm font-medium text-text-secondary-light dark:text-text-secondary mb-1 font-body";

export const codeBlockClass =
  "overflow-auto text-xs p-4 bg-surface-light dark:bg-surface-raised rounded-lg border border-border-subtle-light dark:border-border-subtle font-mono leading-5";

export const inlineCodeClass =
  "bg-surface-overlay-light dark:bg-surface-raised px-1.5 py-0.5 rounded text-xs font-mono text-text-primary-light dark:text-text-primary";

type CodeChildProps = { className?: string; children?: ComponentChildren };

const hasProps = (
  child: unknown,
): child is { props: CodeChildProps } =>
  typeof child === "object" && child !== null && "props" in child;

// react-markdown renders a fenced code block as a `pre` whose single child is a
// `code` element carrying the `language-*` className and the raw code text. This
// extracts that child's props without an `as any` cast.
export const codeChildProps = (
  children: ComponentChildren,
): CodeChildProps => {
  const child = Array.isArray(children) ? children[0] : children;
  return hasProps(child) ? child.props : {};
};

const highlight = (code: string, lang: string) =>
  _hljs?.getLanguage(lang)
    ? _hljs.highlight(code.trim(), { language: lang }).value
    : code.trim();

export const CopyButton = ({ text }: { text: string }) => (
  <Button
    onClick={async () => {
      await navigator.clipboard.writeText(text);
      toast.success("Copied!");
    }}
    className="px-2 py-1 text-xs"
  >
    Copy
  </Button>
);

export const CodeBlock = (
  { code, lang }: { code: string; lang?: string },
) => {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!ref.current) return;
    if (!lang) {
      ref.current.textContent = code.trim();
      return;
    }
    ensureHighlightJs().then(() => {
      if (ref.current) ref.current.innerHTML = highlight(code, lang);
    });
  }, [code, lang]);
  return (
    <div className="relative group">
      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition">
        <CopyButton text={code.trim()} />
      </div>
      {lang && (
        <div className="text-[10px] uppercase tracking-wider text-text-tertiary-light dark:text-text-tertiary mb-1">
          {lang}
        </div>
      )}
      <pre className={codeBlockClass}>
        <code ref={ref} />
      </pre>
    </div>
  );
};

type PreviewChatMessage = {
  id: string;
  authorId: string;
  authorName: string;
  authorAvatar?: string;
  text: string;
  timestamp: number;
};

export const Spinner = ({ className = "h-5 w-5" }: { className?: string }) => (
  <svg
    className={`animate-spin ${className}`}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

export const PageSpinner = () => (
  <div className="flex flex-1 justify-center items-center py-20 w-full h-full">
    <Spinner className="h-10 w-10" />
  </div>
);

import { selectedBot } from "./bots.tsx";

export const BotNotFound = () => {
  const { route } = useLocation();
  return (
    <div className="flex flex-1 flex-col items-center justify-center py-20 gap-3 w-full h-full">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
        className="h-12 w-12 text-text-secondary-light dark:text-text-secondary opacity-50"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M8 15s1.5-2 4-2 4 2 4 2" />
        <line x1="9" y1="9" x2="9.01" y2="9" />
        <line x1="15" y1="9" x2="15.01" y2="9" />
      </svg>
      <p className="text-text-secondary-light dark:text-text-secondary text-sm">
        Bot not found
      </p>
      <button
        type="button"
        className="px-4 py-2 bg-accent/10 dark:bg-accent/20 border border-accent/40 text-accent-light dark:text-accent rounded hover:bg-accent/20 dark:hover:bg-accent/30 transition-colors cursor-pointer text-sm font-medium"
        onClick={() => {
          selectedBot.value = "";
          route("/bots");
        }}
      >
        Back to dashboard
      </button>
    </div>
  );
};

export const InfiniteScrollTrigger = ({
  onIntersect,
  disabled,
}: {
  onIntersect: () => void;
  disabled?: boolean;
}) => {
  const triggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (disabled) return;
    const el = triggerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          onIntersect();
        }
      },
      { rootMargin: "200px" },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [onIntersect, disabled]);

  return <div ref={triggerRef} className="h-4 w-full" />;
};

export const Button = (props: JSX.IntrinsicElements["button"]) => {
  const { className = "", children, disabled, ...rest } = props;
  const hasSelfAlign = typeof className === "string"
    ? className.includes("self-")
    : String(className).includes("self-");
  const base =
    `${
      hasSelfAlign ? "" : "self-start "
    }max-w-max px-4 py-2 font-medium rounded-lg transition-all duration-200 font-body text-sm ` +
    (disabled
      ? "bg-surface-overlay-light dark:bg-surface-overlay text-text-tertiary-light dark:text-text-tertiary cursor-not-allowed opacity-60 "
      : "bg-accent hover:bg-accent-hover text-white cursor-pointer shadow-sm hover:shadow-md ");
  return (
    <button
      {...rest}
      disabled={disabled}
      aria-disabled={disabled}
      className={`${base}${className ? " " + className : ""}`}
    >
      {children}
    </button>
  );
};

type TokenDisplayProps = {
  token: string;
  label: string;
};

export const AnimatedCollapsible = (
  { isOpen, children }: { isOpen: boolean; children: ComponentChildren },
) => (
  <div
    className={`grid transition-[grid-template-rows,opacity] duration-300 ease-in-out ${
      isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
    }`}
  >
    <div className="overflow-hidden">
      {children}
    </div>
  </div>
);

export const DisclosureBadge = ({ isOpen }: { isOpen: boolean }) => (
  <span className="w-6 h-6 rounded-md bg-surface-overlay-light dark:bg-surface-overlay border border-border-subtle-light dark:border-border-subtle flex items-center justify-center text-text-tertiary-light dark:text-text-tertiary group-hover:text-accent group-hover:border-accent/40 transition-all duration-300 shrink-0">
    <svg
      className={`w-3.5 h-3.5 transform transition-transform duration-300 ${
        isOpen ? "rotate-90 text-accent" : ""
      }`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2.5}
        d="M9 5l7 7-7 7"
      />
    </svg>
  </span>
);

type CollapsibleCardProps = {
  title: string;
  subtitle?: ComponentChildren;
  defaultOpen?: boolean;
  children: ComponentChildren;
  className?: string;
  headerRight?: ComponentChildren;
};

export const CollapsibleCard = ({
  title,
  subtitle,
  defaultOpen = false,
  children,
  className = "",
  headerRight,
}: CollapsibleCardProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div
      className={`bg-surface-raised-light dark:bg-surface-raised rounded-lg shadow-md overflow-hidden mb-6 border border-border-subtle-light dark:border-border-subtle ${className}`}
    >
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="group w-full flex items-center justify-between text-left p-6 hover:bg-surface-light dark:hover:bg-surface transition-colors cursor-pointer focus:outline-none"
      >
        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 min-w-0">
          <h2 className="text-xl font-display font-semibold text-text-primary-light dark:text-text-primary group-hover:text-accent transition-colors truncate">
            {title}
          </h2>
          {subtitle && (
            <span className="text-sm text-text-secondary-light dark:text-text-secondary truncate">
              {subtitle}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 shrink-0 ml-4">
          {headerRight}
          <DisclosureBadge isOpen={isOpen} />
        </div>
      </button>
      <AnimatedCollapsible isOpen={isOpen}>
        <div className="border-t border-border-subtle-light dark:border-border-subtle p-6">
          {children}
        </div>
      </AnimatedCollapsible>
    </div>
  );
};

type CollapsibleProps = {
  title: string;
  titleStyle?: CSSProperties;
  style?: CSSProperties;
  children: ComponentChildren;
};

export const Collapsible: FunctionalComponent<CollapsibleProps> = ({
  title,
  style,
  titleStyle,
  children,
}) => (
  <div className="mb-4 min-w-0">
    <div
      className="p-3 bg-surface-overlay-light dark:bg-surface-overlay rounded-lg border border-border-subtle-light dark:border-border-subtle"
      style={titleStyle}
    >
      <span className="font-medium text-text-primary-light dark:text-text-primary text-sm">
        {title}
      </span>
    </div>
    <div style={style} className="mt-3 overflow-x-auto">
      {children}
    </div>
  </div>
);

export const TokenDisplay: FunctionalComponent<TokenDisplayProps> = ({
  token,
  label,
}) => {
  const [visible, setVisible] = useState(false);

  const toggleVisibility = () => setVisible((v) => !v);

  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(token);
    toast.success("Copied!");
  };

  return (
    <div className="flex items-center gap-2 min-w-0 max-w-md">
      <div className="text-sm text-text-secondary-light dark:text-text-secondary whitespace-nowrap">
        {label}
      </div>
      <input
        type={visible ? "text" : "password"}
        readOnly
        value={token}
        className={`flex-1 min-w-0 ${inputBaseClass}`}
      />
      <button
        type="button"
        onClick={toggleVisibility}
        className="px-2 py-1 text-sm rounded-lg border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised hover:bg-surface-overlay-light dark:hover:bg-surface-overlay text-text-secondary-light dark:text-text-secondary transition-colors cursor-pointer"
        aria-label={visible ? "Hide" : "Show"}
        style={{ lineHeight: 0 }}
      >
        {visible
          ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-5 h-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6.75 12a5.25 5.25 0 1 1 10.5 0a5.25 5.25 0 0 1-10.5 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3.75 12s2.25-6.75 8.25-6.75S20.25 12 20.25 12s-2.25 6.75-8.25 6.75S3.75 12 3.75 12z"
              />
            </svg>
          )
          : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-5 h-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M2.25 12s2.25-6.75 9.75-6.75S21.75 12 21.75 12s-2.25 6.75-9.75 6.75S2.25 12 2.25 12z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 15.75a3.75 3.75 0 1 0 0-7.5 3.75 3.75 0 0 0 0 7.5z"
              />
            </svg>
          )}
      </button>
      <button
        type="button"
        onClick={copyToClipboard}
        className="px-2 py-1 text-sm rounded-lg border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised hover:bg-surface-overlay-light dark:hover:bg-surface-overlay text-text-secondary-light dark:text-text-secondary transition-colors cursor-pointer"
        aria-label="Copy"
        style={{ lineHeight: 0 }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="w-5 h-5"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 12h6m-3-3v6m-7.5 2.25V6.75A2.25 2.25 0 0 1 6.75 4.5h6.5A2.25 2.25 0 0 1 15.5 6.75v1.5m-7.5 10.5A2.25 2.25 0 0 0 6.75 19.5h6.5a2.25 2.25 0 0 0 2.25-2.25v-6.5A2.25 2.25 0 0 0 13.25 8.5h-6.5A2.25 2.25 0 0 0 4.5 10.75v6.5z"
          />
        </svg>
      </button>
    </div>
  );
};

export const CopyableValue = (
  { value, label }: { value: string; label?: string },
) => {
  return (
    <div className="flex items-center gap-2 min-w-0">
      {label && (
        <div className="text-sm text-text-secondary-light dark:text-text-secondary">
          {label}
        </div>
      )}
      <span
        className="font-mono text-sm text-text-primary-light dark:text-text-primary truncate max-w-xs"
        title={value}
      >
        {value}
      </span>
      <button
        type="button"
        onClick={async () => {
          await navigator.clipboard.writeText(value);
          toast.success("Copied!");
        }}
        className="px-2 py-1 text-sm rounded-lg border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised hover:bg-surface-overlay-light dark:hover:bg-surface-overlay text-text-secondary-light dark:text-text-secondary transition-colors cursor-pointer"
        aria-label="Copy"
        style={{ lineHeight: 0 }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="w-5 h-5"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 12h6m-3-3v6m-7.5 2.25V6.75A2.25 2.25 0 0 1 6.75 4.5h6.5A2.25 2.25 0 0 1 15.5 6.75v1.5m-7.5 10.5A2.25 2.25 0 0 0 6.75 19.5h6.5a2.25 2.25 0 0 0 2.25-2.25v-6.5A2.25 2.25 0 0 0 13.25 8.5h-6.5A2.25 2.25 0 0 0 4.5 10.75v6.5z"
          />
        </svg>
      </button>
    </div>
  );
};

export const Card = (
  { children, className = "" }: {
    children: ComponentChildren;
    className?: string;
  },
) => (
  <div
    className={`p-5 rounded-xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised ${className}`}
  >
    {children}
  </div>
);

export const CtaButton = ({ onClick, text, className }: {
  onClick: () => void;
  text: string | ComponentChildren;
  className?: string;
}) => (
  <div>
    <Button
      onClick={onClick}
      className={`px-8 py-3 text-base${className ? " " + className : ""}`}
    >
      {text}
    </Button>
  </div>
);

export const ViewConversationsButton = (
  { groupId, label = "View conversations" }: {
    groupId: string;
    label?: string;
  },
) => (
  <Button
    onClick={() => {
      const url = `https://view-chat.com/conversations?groupId=${groupId}`;
      globalThis.open(url, "_blank");
    }}
  >
    {label}
  </Button>
);

export const WebEmbedSnippet = (
  { publicSignKey, initialMessage, ...widgetParams }: {
    publicSignKey: string;
    initialMessage?: string;
    startOpen?: boolean;
    buttonText?: string;
    defaultName?: string;
    colorScheme?: {
      light?: {
        primary?: string;
        background?: string;
        buttonColor?: string;
        buttonTextColor?: string;
      };
      dark?: {
        primary?: string;
        background?: string;
        startButton?: string;
        startButtonText?: string;
      };
    };
  },
) => (
  <div>
    <div className="mb-2 text-sm text-text-secondary-light dark:text-text-secondary">
      Add this script inside the{" "}
      <code className="bg-surface-overlay-light dark:bg-surface-overlay px-1.5 py-0.5 rounded text-sm font-mono text-accent-light dark:text-accent">
        &lt;head&gt;
      </code>{" "}
      tag of your website.
    </div>
    <CopyableValue
      value={embedScript({
        participants: [publicSignKey],
        ...Object.fromEntries(
          Object.entries({ initialMessage, ...widgetParams }).filter((
            [_, v],
          ) => v !== undefined),
        ),
      })}
    />
  </div>
);

const ColorInput = (
  { label, value, onChange, placeholder }: {
    label: string;
    value: string;
    onChange: (v: string) => void;
    placeholder: string;
  },
) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const currentColor = value || placeholder;
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!pickerOpen) return;

    let isDragging = false;

    const handleMouseDown = (e: MouseEvent) => {
      if (pickerRef.current && pickerRef.current.contains(e.target as Node)) {
        isDragging = true;
      } else if (
        pickerRef.current && !pickerRef.current.contains(e.target as Node)
      ) {
        setPickerOpen(false);
      }
    };

    const handleMouseUp = () => {
      if (isDragging) {
        isDragging = false;
        setPickerOpen(false);
      }
    };

    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [pickerOpen]);

  return (
    <div className="text-sm font-medium w-full max-w-md text-text-primary-light dark:text-text-primary">
      <div className="mb-1">{label}</div>
      <div className="flex items-center gap-2">
        <div className="relative">
          <div
            className="w-10 h-10 border border-border-subtle-light dark:border-border-subtle rounded-lg hover:opacity-80 transition cursor-pointer"
            style={{ backgroundColor: currentColor }}
            title={currentColor}
            onClick={() => setPickerOpen((o) => !o)}
          />
          {pickerOpen && (
            <div
              ref={pickerRef}
              className="absolute top-12 left-0 z-50 p-3 bg-surface-raised-light dark:bg-surface-raised rounded-xl shadow-lg border border-border-subtle-light dark:border-border-subtle"
            >
              <HexColorPicker color={currentColor} onChange={onChange} />
            </div>
          )}
        </div>
        <input
          type="text"
          value={value}
          onInput={(e) => onChange(e.currentTarget.value)}
          placeholder={placeholder}
          className={`flex-1 ${inputBaseClass}`}
        />
      </div>
    </div>
  );
};

export const WebEmbedSnippetWithInputs = (
  { publicSignKey }: { publicSignKey: string },
) => {
  const [initialMessage, setInitialMessage] = useState("");
  const [startOpen, setStartOpen] = useState(false);
  const [buttonText, setButtonText] = useState("");
  const [defaultName, setDefaultName] = useState("");
  const [colorMode, setColorMode] = useState<"light" | "dark">("light");

  const [lightPrimary, setLightPrimary] = useState("");
  const [lightBackground, setLightBackground] = useState("");
  const [lightButtonColor, setLightButtonColor] = useState("");
  const [lightButtonTextColor, setLightButtonTextColor] = useState("");

  const [darkPrimary, setDarkPrimary] = useState("");
  const [darkBackground, setDarkBackground] = useState("");
  const [darkStartButton, setDarkStartButton] = useState("");
  const [darkStartButtonText, setDarkStartButtonText] = useState("");

  const buildColorScheme = () => {
    const light = {
      ...(lightPrimary && { primary: lightPrimary }),
      ...(lightBackground && { background: lightBackground }),
      ...(lightButtonColor && { buttonColor: lightButtonColor }),
      ...(lightButtonTextColor && { buttonTextColor: lightButtonTextColor }),
    };

    const dark = {
      ...(darkPrimary && { primary: darkPrimary }),
      ...(darkBackground && { background: darkBackground }),
      ...(darkStartButton && { startButton: darkStartButton }),
      ...(darkStartButtonText && { startButtonText: darkStartButtonText }),
    };

    const hasLight = Object.keys(light).length > 0;
    const hasDark = Object.keys(dark).length > 0;

    if (!hasLight && !hasDark) return undefined;

    return {
      ...(hasLight && { light }),
      ...(hasDark && { dark }),
    };
  };

  const colorScheme = buildColorScheme();

  const selectedScheme = colorMode === "dark"
    ? colorScheme?.dark
    : colorScheme?.light;

  const previewTextColor = colorMode === "light"
    ? colorScheme?.light?.buttonTextColor
    : colorScheme?.dark?.startButtonText;

  const previewCustomColors = selectedScheme
    ? {
      ...(selectedScheme.primary && { primary: selectedScheme.primary }),
      ...(selectedScheme.background &&
        { background: selectedScheme.background }),
      ...(previewTextColor && { text: previewTextColor }),
    }
    : undefined;

  const previewMessages: PreviewChatMessage[] = [
    {
      id: "preview-1",
      authorId: "user",
      authorName: defaultName || "You",
      text: initialMessage || "hello",
      timestamp: Date.now() - 1000 * 60,
    },
    {
      id: "preview-2",
      authorId: "bot",
      authorName: "Bot",
      text: "Hi! This is a live preview.",
      timestamp: Date.now(),
    },
  ];

  return (
    <div className="flex flex-col gap-6 max-w-5xl">
      <div className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed">
        Customize the widget's appearance and behavior to match your site.
      </div>

      <div className="flex flex-col gap-3">
        <div className="text-base font-semibold text-text-primary-light dark:text-text-primary">
          Color mode
        </div>
        <div className="flex flex-wrap gap-3">
          <label className="flex items-center gap-2 cursor-pointer text-sm text-text-secondary-light dark:text-text-secondary">
            <input
              type="radio"
              name="colorMode"
              value="light"
              checked={colorMode === "light"}
              onChange={() => setColorMode("light")}
              className="cursor-pointer accent-accent"
            />
            Light
          </label>
          <label className="flex items-center gap-2 cursor-pointer text-sm text-text-secondary-light dark:text-text-secondary">
            <input
              type="radio"
              name="colorMode"
              value="dark"
              checked={colorMode === "dark"}
              onChange={() => setColorMode("dark")}
              className="cursor-pointer accent-accent"
            />
            Dark
          </label>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="rounded-xl border border-border-subtle-light dark:border-border-subtle p-5 bg-surface-raised-light dark:bg-surface-raised">
          <div className="text-base font-semibold mb-4 text-text-primary-light dark:text-text-primary">
            Start button
          </div>
          <div className="flex flex-col gap-4">
            <div className="text-sm font-medium w-full text-text-primary-light dark:text-text-primary">
              Button text
              <input
                type="text"
                dir="auto"
                value={buttonText}
                onInput={(e) => setButtonText(e.currentTarget.value)}
                placeholder="e.g., Start Chat"
                className={`mt-1 w-full ${inputBaseClass}`}
              />
              <div className="text-xs text-text-tertiary-light dark:text-text-tertiary mt-1">
                Defaults to "Start Chat" if empty.
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm font-medium text-text-primary-light dark:text-text-primary">
              <input
                type="checkbox"
                checked={startOpen}
                onChange={(e) => setStartOpen(e.currentTarget.checked)}
                className="rounded accent-accent"
              />
              Start widget open
            </label>

            <div className="flex flex-col gap-3">
              {colorMode === "light"
                ? (
                  <>
                    <ColorInput
                      label="Button color"
                      value={lightButtonColor}
                      onChange={setLightButtonColor}
                      placeholder="#0084ff"
                    />
                    <ColorInput
                      label="Button text color"
                      value={lightButtonTextColor}
                      onChange={setLightButtonTextColor}
                      placeholder="#ffffff"
                    />
                  </>
                )
                : (
                  <>
                    <ColorInput
                      label="Button color"
                      value={darkStartButton}
                      onChange={setDarkStartButton}
                      placeholder="#0084ff"
                    />
                    <ColorInput
                      label="Button text color"
                      value={darkStartButtonText}
                      onChange={setDarkStartButtonText}
                      placeholder="#ffffff"
                    />
                  </>
                )}
            </div>

            <div className="border-t border-border-subtle-light dark:border-border-subtle pt-4 mt-2">
              <div className="text-sm font-semibold mb-3 text-text-primary-light dark:text-text-primary">
                Preview
              </div>
              <div
                className="px-4 py-2 rounded-full font-medium cursor-pointer inline-flex items-center justify-center text-sm"
                style={{
                  backgroundColor: colorMode === "light"
                    ? (lightButtonColor || "#0084ff")
                    : (darkStartButton || "#0084ff"),
                  color: colorMode === "light"
                    ? (lightButtonTextColor || "#ffffff")
                    : (darkStartButtonText || "#ffffff"),
                }}
              >
                {buttonText || "Start Chat"}
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-border-subtle-light dark:border-border-subtle p-5 bg-surface-raised-light dark:bg-surface-raised">
          <div className="text-base font-semibold mb-4 text-text-primary-light dark:text-text-primary">
            Chat interface
          </div>
          <div className="flex flex-col gap-4">
            <div className="text-sm font-medium w-full text-text-primary-light dark:text-text-primary">
              Initial user message
              <input
                type="text"
                dir="auto"
                value={initialMessage}
                onInput={(e) => setInitialMessage(e.currentTarget.value)}
                placeholder="hello"
                className={`mt-1 w-full ${inputBaseClass}`}
              />
              <div className="text-xs text-text-tertiary-light dark:text-text-tertiary mt-1">
                Sent on behalf of the user to kick off the conversation.
              </div>
            </div>

            <div className="text-sm font-medium w-full text-text-primary-light dark:text-text-primary">
              User name in chat
              <input
                type="text"
                dir="auto"
                value={defaultName}
                onInput={(e) => setDefaultName(e.currentTarget.value)}
                placeholder="You"
                className={`mt-1 w-full ${inputBaseClass}`}
              />
              <div className="text-xs text-text-tertiary-light dark:text-text-tertiary mt-1">
                If empty, the user will be asked before the chat opens.
              </div>
            </div>

            <div className="flex flex-col gap-3">
              {colorMode === "light"
                ? (
                  <>
                    <ColorInput
                      label="Primary color"
                      value={lightPrimary}
                      onChange={setLightPrimary}
                      placeholder="#0084ff"
                    />
                    <ColorInput
                      label="Background color"
                      value={lightBackground}
                      onChange={setLightBackground}
                      placeholder="#ffffff"
                    />
                  </>
                )
                : (
                  <>
                    <ColorInput
                      label="Primary color"
                      value={darkPrimary}
                      onChange={setDarkPrimary}
                      placeholder="#0084ff"
                    />
                    <ColorInput
                      label="Background color"
                      value={darkBackground}
                      onChange={setDarkBackground}
                      placeholder="#1a1a1a"
                    />
                  </>
                )}
            </div>

            <div className="border-t border-border-subtle-light dark:border-border-subtle pt-4 mt-2">
              <div className="text-sm font-semibold mb-3 text-text-primary-light dark:text-text-primary">
                Preview
              </div>
              <div className="flex" style={{ height: "384px" }}>
                <AbstractChatBox
                  userId={publicSignKey}
                  onSend={() => {}}
                  messages={previewMessages}
                  canLoadMore={false}
                  loadMore={() => {}}
                  title="Chat"
                  customColors={previewCustomColors}
                  darkModeOverride={colorMode === "dark"}
                  disableAutoFocus
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-xl border-2 border-accent/30 p-6 bg-accent-muted">
        <div className="text-lg font-bold mb-2 text-accent-light dark:text-accent font-display">
          Generate & Update Embed Snippet
        </div>
        <div className="mb-4 p-3 rounded-lg bg-accent-muted border border-accent/20 text-sm text-text-primary-light dark:text-text-primary">
          <strong>Important:</strong>{" "}
          Every time you make changes to the configuration above, you need to
          copy the updated snippet below and re-embed it on your website for the
          changes to take effect.
        </div>
        <WebEmbedSnippet
          publicSignKey={publicSignKey}
          {...(initialMessage && { initialMessage })}
          {...(buttonText && { buttonText })}
          {...(defaultName && { defaultName })}
          {...(colorScheme && { colorScheme })}
          startOpen={startOpen}
        />
      </div>
    </div>
  );
};

const legalLinkStyle =
  "hover:text-accent-light dark:hover:text-accent transition-colors";

const DiscordIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
  </svg>
);

export const legalLinks = [
  { href: "/pricing", label: "Pricing" },
  { href: "/blog", label: "Blog" },
  { href: termsPath, label: "Terms of Service" },
  { href: "/refund-policy", label: "Refund Policy" },
  { href: privacyPath, label: "Privacy" },
  { href: "/about", label: "About" },
];

export const LegalFooter = () => (
  <footer className="mt-auto text-center text-xs text-text-tertiary-light dark:text-text-tertiary py-6 space-x-3 hidden sm:flex sm:items-center sm:justify-center">
    {legalLinks.flatMap(({ href, label }, i) => [
      ...(i > 0
        ? [
          <span
            key={`sep-${i}`}
            className="text-border-default-light dark:text-border-default"
          >
            &middot;
          </span>,
        ]
        : []),
      <a key={href} href={href} className={legalLinkStyle}>{label}</a>,
    ])}
    <span className="text-border-default-light dark:text-border-default">
      &middot;
    </span>
    <a
      href={discordLink}
      target="_blank"
      rel="noopener noreferrer"
      className="hover:text-accent-light dark:hover:text-accent transition-colors inline-flex items-center gap-1"
      title="Join our Discord"
    >
      <DiscordIcon className="w-3.5 h-3.5" />
      Discord
    </a>
  </footer>
);

export interface ConfirmState {
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  title?: string;
  confirmText?: string;
  cancelText?: string;
}

export const confirmState = signal<ConfirmState | null>(null);

export const customConfirm = (
  message: string,
  options?: { title?: string; confirmText?: string; cancelText?: string },
): Promise<boolean> => {
  return new Promise((resolve) => {
    confirmState.value = {
      message,
      title: options?.title || "Confirm Action",
      confirmText: options?.confirmText || "Confirm",
      cancelText: options?.cancelText || "Cancel",
      onConfirm: () => {
        confirmState.value = null;
        resolve(true);
      },
      onCancel: () => {
        confirmState.value = null;
        resolve(false);
      },
    };
  });
};

export const ArrowRightIcon = (
  { className = "w-4 h-4" }: { className?: string },
) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

export const ConfirmDialog = () => {
  const state = confirmState.value;
  if (!state) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50"
      onClick={state.onCancel}
    >
      <div
        className="relative w-full max-w-md bg-surface-raised-light dark:bg-surface-raised border border-border-subtle-light dark:border-border-subtle rounded-xl shadow-2xl p-6 flex flex-col gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        {state.title && (
          <h3 className="text-lg font-semibold text-text-primary-light dark:text-text-primary font-body">
            {state.title}
          </h3>
        )}
        <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed font-body whitespace-pre-wrap">
          {state.message}
        </p>
        <div className="flex justify-end gap-3 mt-2">
          <Button
            type="button"
            onClick={state.onCancel}
            className="bg-transparent hover:bg-surface-overlay-light dark:hover:bg-surface-overlay border border-border-subtle-light dark:border-border-subtle !text-text-primary-light dark:!text-text-primary shadow-none hover:shadow-none"
          >
            {state.cancelText}
          </Button>
          <Button
            type="button"
            onClick={state.onConfirm}
            className="bg-accent hover:bg-accent-hover text-white"
          >
            {state.confirmText}
          </Button>
        </div>
      </div>
    </div>
  );
};

const logoFile = "/logo.png";

export const Logo = (
  { className = "", clickable = true }: {
    className?: string;
    clickable?: boolean;
  },
) => {
  const { route } = useLocation();
  return (
    <div
      className={`font-bold text-xl select-none flex items-center gap-1.5 ${
        clickable ? "cursor-pointer" : "cursor-default"
      } ${className}`}
      onClick={clickable ? () => route("/") : undefined}
      title={clickable ? "Go to Home" : undefined}
      style={{ lineHeight: 1.1 }}
    >
      <img
        src={logoFile}
        alt="prompt2bot logo"
        className="h-[1.4em] w-[1.4em]"
        style={{ display: "inline-block" }}
      />
      <span className="font-display text-accent-light dark:text-accent-hover">
        prompt2<span className="text-accent-light dark:text-accent">bot</span>
      </span>
    </div>
  );
};
